import {
  IconArrowRight,
  IconCheck,
  IconDownload,
  IconRadar2,
  IconUpload,
} from "@tabler/icons-react";

function StepBadge({ num }) {
  return (
    <span className="inline-flex h-6 items-center rounded-full bg-white/8 px-2.5 font-mono text-[11px] font-semibold tracking-widest text-text-soft">
      {num}
    </span>
  );
}

export function HowItWorks() {
  return (
    <section
      className="relative overflow-hidden px-6 py-32 sm:px-8 sm:py-40"
    >
      {/* Ambient glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute -right-20 top-1/2 h-110 w-140 -translate-y-1/2 rounded-full bg-main-500/8 blur-[140px]"
      />

      <div id="how-it-works" className="relative mx-auto max-w-360 scroll-mt-24">
        {/* Header */}
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-main-300">
            How it works
          </p>
          <h2 className="text-display-gradient mt-3 text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl">
            From a county list
            <br />
            to a ready-to-dial list.
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-base text-text-soft">
            No setup, no SDK install, no onboarding calls. Drop your first
            property list and get verified owner contacts back in under a
            minute.
          </p>
        </div>

        {/* Bento grid */}
        <div className="mt-16 grid grid-cols-12 gap-4">
          {/* ── 01 Upload ── col-span-6 */}
          <article className="col-span-12 md:col-span-6 flex flex-col overflow-hidden rounded-2xl bg-card p-7 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.07),inset_0_1px_0_0_rgba(255,255,255,0.06)] sm:p-8">
            <div className="flex items-center justify-between">
              <div className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-main-500/10 shadow-[inset_0_0_0_1px_rgba(63,96,214,0.2)]">
                <IconUpload size={20} stroke={1.7} className="text-main-300" />
              </div>
              <StepBadge num="01" />
            </div>

            <h3 className="mt-7 text-xl font-semibold tracking-tight text-text">
              Upload
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-text-soft">
              Drop a county list, an MLS export, or paste a single parcel —
              we take it any way you've got it.
            </p>

            <ul className="mt-6 space-y-2 border-t border-white/6 pt-5">
              {["CSV bulk uploads", "REST API + single-address", "Direct from Propstream / Privy"].map((pt) => (
                <li key={pt} className="flex items-start gap-2 text-xs text-text-muted">
                  <IconCheck size={11} stroke={2.6} className="mt-0.75 shrink-0 text-main-300" />
                  <span>{pt}</span>
                </li>
              ))}
            </ul>
          </article>

          {/* ── 02 Trace ── col-span-6, featured (brand-filled) */}
          <article className="col-span-12 md:col-span-6 relative flex flex-col overflow-hidden rounded-2xl bg-main-500 p-7 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.18)] sm:p-8">
            {/* Inner glow */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 bg-linear-to-br from-white/10 via-transparent to-transparent"
            />
            {/* Subtle dot texture */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 opacity-20"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.25) 1px, transparent 0)",
                backgroundSize: "24px 24px",
              }}
            />

            <div className="relative flex items-center justify-between">
              <div className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/15 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.2)]">
                <IconRadar2 size={20} stroke={1.7} className="text-white" />
              </div>
              <span className="inline-flex h-6 items-center rounded-full bg-white/15 px-2.5 font-mono text-[11px] font-semibold tracking-widest text-white/80">
                02
              </span>
            </div>

            <h3 className="relative mt-7 text-xl font-semibold tracking-tight text-white">
              Trace
            </h3>
            <p className="relative mt-2 text-sm leading-relaxed text-white/75">
              We resolve each property to the actual owner in milliseconds —
              DNC and litigation scrubbed automatically.
            </p>

            {/* Check badges */}
            <div className="relative mt-8 flex flex-wrap gap-2.5">
              {[
                "Owner resolved",
                "Phone verified",
                "Emails matched",
                "LLC principals",
                "DNC checked",
                "Litigation scrubbed",
              ].map((label) => (
                <span
                  key={label}
                  className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1.5 text-xs font-medium text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.2)]"
                >
                  <IconCheck size={10} stroke={3} className="text-white" />
                  {label}
                </span>
              ))}
            </div>

          </article>

          {/* ── 03 Download ── col-span-12, full width */}
          <article className="col-span-12 flex flex-col overflow-hidden rounded-2xl bg-card p-7 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.07),inset_0_1px_0_0_rgba(255,255,255,0.06)] sm:p-8">
            <div className="flex items-start justify-between gap-6">
              <div>
                <div className="flex items-center gap-3">
                  <div className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-main-500/10 shadow-[inset_0_0_0_1px_rgba(63,96,214,0.2)]">
                    <IconDownload size={20} stroke={1.7} className="text-main-300" />
                  </div>
                  <StepBadge num="03" />
                </div>
                <h3 className="mt-5 text-xl font-semibold tracking-tight text-text">
                  Download
                </h3>
                <p className="mt-2 max-w-md text-sm leading-relaxed text-text-soft">
                  Pull verified contacts back ready to dial — or pipe them
                  straight into your CRM and dialer.
                </p>
              </div>

              {/* Output options grid */}
              <div className="hidden lg:grid grid-cols-4 gap-3 flex-1 max-w-xl self-center">
                {[
                  { label: "CSV export", sub: "Instant download" },
                  { label: "CRM push", sub: "HubSpot · Follow Up Boss" },
                  { label: "Dialer list", sub: "Mojo · BatchDialer" },
                  { label: "API stream", sub: "Webhook + REST" },
                ].map((opt) => (
                  <div
                    key={opt.label}
                    className="flex flex-col rounded-xl bg-bg p-4 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.06)]"
                  >
                    <span className="text-[11px] font-semibold text-text">
                      {opt.label}
                    </span>
                    <span className="mt-1 text-[10px] leading-snug text-text-muted">
                      {opt.sub}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Mobile output list */}
            <div className="mt-6 grid grid-cols-2 gap-3 lg:hidden">
              {[
                { label: "CSV export", sub: "Instant download" },
                { label: "CRM push", sub: "HubSpot · Follow Up Boss" },
                { label: "Dialer list", sub: "Mojo · BatchDialer" },
                { label: "API stream", sub: "Webhook + REST" },
              ].map((opt) => (
                <div
                  key={opt.label}
                  className="flex flex-col rounded-xl bg-bg p-4 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.06)]"
                >
                  <span className="text-[11px] font-semibold text-text">
                    {opt.label}
                  </span>
                  <span className="mt-1 text-[10px] leading-snug text-text-muted">
                    {opt.sub}
                  </span>
                </div>
              ))}
            </div>

            <ul className="mt-6 flex flex-wrap gap-x-6 gap-y-2 border-t border-white/6 pt-5">
              {["Direct CSV download", "Streaming webhooks", "CRM + dialer integrations"].map((pt) => (
                <li key={pt} className="flex items-center gap-2 text-xs text-text-muted">
                  <IconCheck size={11} stroke={2.6} className="shrink-0 text-main-300" />
                  <span>{pt}</span>
                </li>
              ))}
            </ul>
          </article>
        </div>

        {/* CTA row */}
        <div className="mt-14 flex flex-wrap items-center justify-center gap-5">
          <a
            href="https://app.titanskip.com"
            className="group inline-flex h-11 items-center gap-2 rounded-full bg-main-500 pl-5 pr-4 text-sm font-semibold text-white transition hover:bg-main-400"
          >
            Start tracing
            <span
              aria-hidden
              className="grid h-6 w-6 place-items-center rounded-full bg-white/15 transition group-hover:translate-x-0.5 group-hover:bg-white/25"
            >
              <IconArrowRight size={12} stroke={2.6} />
            </span>
          </a>
        </div>
      </div>
    </section>
  );
}
